import { useRef } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useTranslation } from 'react-i18next';

import { Container, ChartWrapper, EmptyState } from './index.styles';
import { useContainerHeight } from '../useContainerHeight';
import { resolveCategoryLabel, resolveDayOfWeek, formatCompact, chartLegendFormatter } from '../../../../helpers';
import ProfileIcons from '../../../ProfileIcons';
import { SCATTER_CATEGORY_COLORS, SCATTER_DEFAULT_COLORS, CHART_GRID_COLOR_DARK, CHART_TICK_COLOR } from '../consts';
import { ChartTooltip } from '../TooltipComponent';

const SCATTER_COLORS: Record<string, string> = SCATTER_CATEGORY_COLORS;
const DEFAULT_COLORS = SCATTER_DEFAULT_COLORS;
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_TICKS = [0, 1, 2, 3, 4, 5, 6];
const BUBBLE_RANGE: [number, number] = [40, 400];

// Reading fields by a runtime key needs a string index signature; intersecting the real
// datum with Record adds one (and stays a subtype of ScatterDataPoint, so the cast is legal).
type IndexableDatum = ScatterDataPoint & Record<string, string | number>;

export const ScatterRenderer = ({ data, selectedProfiles, supportedPlatforms }: DynamicModuleBuilderProps) => {
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const chartHeight = useContainerHeight(chartWrapperRef as React.RefObject<HTMLDivElement>);
  const { t } = useTranslation('reports');

  if (!data || data.chartType !== 'Scatter') {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  // axis + legend drive field access and series order instead of hardcoded names.
  const moduleData = data as ScatterModuleData & {
    axis?: {
      x?: { dataKey?: string; type?: string };
      y?: { dataKey?: string; type?: string };
    };
    legend?: { dataKey?: string; name?: string; items?: string[] };
  };

  const { data: scatterData, legend, axis } = moduleData;

  if (!scatterData || scatterData.length === 0) {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const rows = scatterData as IndexableDatum[];

  const xKey = axis?.x?.dataKey ?? 'date';
  const yKey = axis?.y?.dataKey ?? 'value';
  const xType = axis?.x?.type ?? 'date';
  const categoryKey = legend?.dataKey ?? 'category';
  const isDateAxis = xType === 'date';
  const categories: string[] = legend?.items ?? [...new Set(rows.map((d) => String(d[categoryKey])))];

  const groupedData = categories.map((cat, idx) => ({
    category: cat,
    color: SCATTER_COLORS[cat] ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
    points: rows
      .filter((d) => String(d[categoryKey]) === cat)
      .map((d) => {
        const rawX = d[xKey];

        return {
          x: isDateAxis ? new Date(rawX).getDay() : Number(rawX), // date → 0..6 (Sun..Sat)
          y: Number(d[yKey]),
          rawX, // kept for the tooltip
          category: cat,
        };
      }),
  }));

  // X axis adapts to the configured type: day-of-week buckets for dates, plain numeric otherwise.
  const xAxisProps = {
    domain: (isDateAxis ? [-0.5, 6.5] : ['auto', 'auto']) as [number, number] | [string, string],
    ticks: isDateAxis ? DAY_TICKS : undefined,
    interval: isDateAxis ? (0 as const) : undefined,
    tickFormatter: isDateAxis ? (i: number) => resolveDayOfWeek(DAY_KEYS[i], t).slice(0, 3) : formatCompact,
  };

  return (
    <Container>
      <ChartWrapper ref={chartWrapperRef}>
        <ResponsiveContainer
          width="100%"
          height={chartHeight || '100%'}
        >
          <ScatterChart margin={{ top: 25, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid stroke={CHART_GRID_COLOR_DARK} />
            <XAxis
              type="number"
              dataKey="x"
              tick={{ fontSize: 12, fill: CHART_TICK_COLOR }}
              axisLine={false}
              tickLine={false}
              {...xAxisProps}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 'auto']}
              tickCount={11}
              tick={{ fontSize: 12, fill: CHART_TICK_COLOR, textAnchor: 'end' }}
              tickFormatter={formatCompact}
              axisLine={false}
              tickLine={false}
            />
            <ZAxis
              dataKey="y"
              range={BUBBLE_RANGE}
            />
            <Tooltip
              content={
                <ChartTooltip
                  variant="scatter"
                  payloadFilter={(entry) => entry.dataKey === 'y'}
                  getTitle={(entry) => entry.payload.category}
                  getLabel={(entry) => (entry.payload as Record<string, unknown>)?.rawX}
                  valueFormatter={(v) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}
                />
              }
            />
            <Legend
              iconType="circle"
              iconSize={12}
              align="left"
              wrapperStyle={{
                fontSize: '12px',
                paddingTop: '0.5rem',
                marginLeft: '20px',
                marginBottom: '3px',
              }}
              formatter={chartLegendFormatter}
            />

            {groupedData.map((group) => (
              <Scatter
                key={group.category}
                name={resolveCategoryLabel(group.category, t)}
                data={group.points}
                fill={group.color}
                fillOpacity={0.9}
                isAnimationActive={false}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </ChartWrapper>
      <ProfileIcons
        isChartType
        selectedProfiles={selectedProfiles}
        supportedPlatforms={supportedPlatforms}
      />
    </Container>
  );
};
